import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Maximize2,
  MessageSquare,
  Minimize2,
  Minus,
  PanelRight,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import {
  useDashboardAssistantDock,
  type DashboardAssistantDockMessage,
  type DashboardAssistantDockSession,
} from '../../../../contexts/DashboardAssistantDockContext';
import { setAssistantFeedback } from '../../../../api/dashboardAssistantHistory';
import { getActiveOrganizationContext } from '../lib/organizationContext';
import { getDockMessageKey, isSessionHistoryRegression } from '../lib/messageModel';
import { getSessionTitle } from '../lib/conversationHistory';
import {
  getDocumentationSourceUrls,
  getUseListItemId,
  groupDashboardMessageRenderItems,
  hasPendingUseListCard,
} from '../lib/activityHelpers';
import {
  getPageAwareAnalyticsSuggestions,
  getSmartTaggingSuggestions,
  getSuggestedRepliesForLatestAssistantMessage,
} from '../lib/chatSuggestions';
import { useDashboardAssistantChat } from '../hooks/useDashboardAssistantChat';
import { isSmartTaggingSession } from '../../../../utils/smartTaggingSessions';
import { safeCapture } from '../../../../utils/analytics';
import { isInngestAssistantExecutionEnabled } from '../../../../config/dashboardAssistantExecution';
import {
  AssistantMessageContent,
  DashboardActivityGroup,
  DashboardAssistantThinkingIndicator,
} from './ActivityGroup';

const DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID =
  import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID ||
  import.meta.env.VITE_DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID ||
  '';

const DASHBOARD_ASSISTANT_FEEDBACK_SENTIMENT_QUESTION_ID =
  import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_FEEDBACK_SENTIMENT_QUESTION_ID ||
  import.meta.env.VITE_DASHBOARD_ASSISTANT_FEEDBACK_SENTIMENT_QUESTION_ID ||
  'sentiment';

function getHistoricalAssistantMessageKeys(messages: DashboardAssistantDockMessage[] = []) {
  return new Set(
    messages
      .filter((message) => message.role === 'assistant' && message.kind !== 'action')
      .map(getDockMessageKey),
  );
}

export function DashboardAssistantChatSessionPanel({
  dashboardTimezone,
  merchantCountryCode,
  compact = false,
  isDocked,
  isMaximized,
  onToggleDocked,
  onToggleMaximized,
  session,
}: {
  dashboardTimezone?: string | null;
  merchantCountryCode?: string | null;
  compact?: boolean;
  isDocked: boolean;
  isMaximized: boolean;
  onToggleDocked: () => void;
  onToggleMaximized: () => void;
  session: DashboardAssistantDockSession;
}) {
  const { closeSession, minimize, updateSession, drafts, setSessionDraft, clearSubmittedDraft, claimInitialPrompt } = useDashboardAssistantDock();
  const location = useLocation();
  const posthog = usePostHog();
  const { organization } = useOrganization();
  const { session: clerkSession } = useSession();
  const { user } = useUser();
  const memberships = user?.organizationMemberships || [];
  const { organizationId: activeOrganizationId } = getActiveOrganizationContext({
    memberships,
    organization,
    sessionOrganizationId: clerkSession?.lastActiveOrganizationId,
  });
  const {
    canCancelActiveRun,
    cancelActiveRun,
    messages,
    sendMessage,
    isLoading,
    isReady,
    error,
    conversationId,
    statusMessage,
  } = useDashboardAssistantChat(session);
  const draft = drafts[session.id] || '';
  const setDraft = (value: string) => setSessionDraft(session.id, value);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, 'up' | 'down'>>(
    () =>
      Object.fromEntries(
        (session.historyMessages ?? [])
          .filter((message) => message.id && message.feedback)
          .map((message) => [message.id as string, message.feedback as 'up' | 'down']),
      ),
  );
  const [feedbackSavingByMessageId, setFeedbackSavingByMessageId] = useState<
    Record<string, boolean>
  >({});
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const persistedMessagesRef = useRef('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const animatedAssistantMessageIdsRef = useRef<Set<string>>(
    getHistoricalAssistantMessageKeys(session.historyMessages),
  );
  const feedbackSurveyShownTraceIdsRef = useRef<Set<string>>(new Set());
  const [animatingAssistantMessageKeys, setAnimatingAssistantMessageKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const displayedMessages = messages;
  const displayedMessagesWithFeedback = useMemo(
    () =>
      displayedMessages.map((message) =>
        message.id && feedbackByMessageId[message.id]
          ? { ...message, feedback: feedbackByMessageId[message.id] }
          : message,
      ),
    [displayedMessages, feedbackByMessageId],
  );
  const displayedRenderItems = useMemo(
    () => groupDashboardMessageRenderItems(displayedMessagesWithFeedback),
    [displayedMessagesWithFeedback],
  );
  const useListItemId = useMemo(() => getUseListItemId(displayedRenderItems), [displayedRenderItems]);
  // Smart Tagging sessions (AI Tag Automation) are ordinary chat sessions —
  // the chat IS the harness. The only per-session adaptations are the chips
  // (session-aware, like the page-aware analytics sets), the greeting, and a
  // stable title; everything else renders identically to any other session.
  const isSmartTagging = isSmartTaggingSession(session.id);
  const emptyStateSuggestions = useMemo(
    () =>
      isSmartTagging
        ? getSmartTaggingSuggestions()
        : getPageAwareAnalyticsSuggestions(location.pathname),
    [isSmartTagging, location.pathname],
  );
  const suggestedReplies = useMemo(() => {
    // While a segment card waits for Use this list, its button is the only
    // way on: a chip would skip the button's save of pending unchecks (C1).
    if (draft.trim() || isLoading || hasPendingUseListCard(displayedRenderItems)) {
      return [];
    }

    return getSuggestedRepliesForLatestAssistantMessage(displayedMessagesWithFeedback);
  }, [displayedMessagesWithFeedback, displayedRenderItems, draft, isLoading]);

  useEffect(() => {
    shouldStickToBottomRef.current = true;
    feedbackSurveyShownTraceIdsRef.current = new Set();
    animatedAssistantMessageIdsRef.current = getHistoricalAssistantMessageKeys(
      session.historyMessages,
    );
    setAnimatingAssistantMessageKeys(new Set());
    // Session identity is the reset boundary. History changes continuously
    // during a run and must not mark a newly arrived reply as historical.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    if (!posthog || !DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID) {
      return;
    }

    for (const message of displayedMessagesWithFeedback) {
      if (message.role !== 'assistant' || message.kind === 'action' || !message.id) {
        continue;
      }

      const aiFeedbackProperties = getAiFeedbackProperties(
        message,
        displayedMessagesWithFeedback,
        session.id,
      );
      const traceId = aiFeedbackProperties.$ai_trace_id;
      if (!traceId || feedbackSurveyShownTraceIdsRef.current.has(traceId)) {
        continue;
      }

      feedbackSurveyShownTraceIdsRef.current.add(traceId);
      safeCapture(posthog, 'survey shown', {
        ...aiFeedbackProperties,
        $survey_id: DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID,
        entry_id: message.id,
        organization_id: activeOrganizationId,
        source: 'dashboard_assistant_frontend',
        thread_id: session.id,
      });
    }
  }, [activeOrganizationId, displayedMessagesWithFeedback, posthog, session.id]);

  useEffect(() => {
    // assistant-ui exposes token deltas while loading, so do not replay those
    // messages with a typewriter after the stream completes. Inngest delivers
    // the final persisted reply as one complete message while the run is still
    // loading; leave that message unmarked so it animates once at completion.
    if (!isLoading || isInngestAssistantExecutionEnabled()) {
      return;
    }

    const latestItem = displayedRenderItems[displayedRenderItems.length - 1];
    if (latestItem?.type !== 'message') {
      return;
    }

    const latestMessage = latestItem.message;
    if (latestMessage.role === 'assistant' && latestMessage.kind !== 'action') {
      animatedAssistantMessageIdsRef.current.add(getDockMessageKey(latestMessage));
    }
  }, [displayedRenderItems, isLoading]);

  const latestAssistantAnimationKey = useMemo(() => {
    const latestItem = displayedRenderItems[displayedRenderItems.length - 1];
    if (latestItem?.type !== 'message' || isLoading) {
      return null;
    }

    const latestMessage = latestItem.message;
    if (
      latestMessage.role !== 'assistant' ||
      latestMessage.kind === 'action' ||
      latestMessage.displayPayload ||
      !latestMessage.content
    ) {
      return null;
    }

    const latestMessageKey = getDockMessageKey(latestMessage);
    return animatedAssistantMessageIdsRef.current.has(latestMessageKey) ? null : latestMessageKey;
  }, [displayedRenderItems, isLoading]);

  useEffect(() => {
    if (!latestAssistantAnimationKey) {
      return;
    }

    setAnimatingAssistantMessageKeys((currentKeys) => {
      if (currentKeys.has(latestAssistantAnimationKey)) {
        return currentKeys;
      }

      const nextKeys = new Set(currentKeys);
      nextKeys.add(latestAssistantAnimationKey);
      return nextKeys;
    });
  }, [latestAssistantAnimationKey]);

  useEffect(() => {
    if (conversationId && conversationId !== session.conversationId) {
      updateSession(session.id, { conversationId });
    }
  }, [conversationId, session.conversationId, session.id, updateSession]);

  useEffect(() => {
    if (!shouldStickToBottomRef.current) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [displayedMessages.length, isLoading]);

  useEffect(() => {
    if (displayedMessages.length === 0) {
      return;
    }
    if (isLoading) {
      return;
    }

    const serializedMessages = JSON.stringify(displayedMessagesWithFeedback);
    if (serializedMessages === persistedMessagesRef.current) {
      return;
    }
    if (isSessionHistoryRegression(session.historyMessages ?? [], displayedMessagesWithFeedback)) {
      return;
    }

    persistedMessagesRef.current = serializedMessages;
    updateSession(session.id, {
      historyMessages: displayedMessagesWithFeedback,
      lastMessageAt: new Date().toISOString(),
    });
  }, [
    displayedMessages.length,
    displayedMessagesWithFeedback,
    isLoading,
    session.historyMessages,
    session.id,
    updateSession,
  ]);

  useEffect(() => {
    if (error) {
      setInlineError(error.message || 'Chat failed to send.');
    }
  }, [error]);

  useEffect(() => {
    if (compact) return;
    const frameId = window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [compact, isMaximized, session.id]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = '40px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 144)}px`;
  }, [draft]);

  // Stable title for Smart Tagging sessions — the server derives thread
  // titles from the first message, which would otherwise replace it.
  const panelTitle = useMemo(
    () => (isSmartTagging ? 'Smart Tagging' : session.title || 'New chat'),
    [isSmartTagging, session.title],
  );

  const handleFeedback = async (messageId: string, feedback: 'up' | 'down') => {
    const feedbackMessage = displayedMessagesWithFeedback.find(
      (message) => message.id === messageId,
    );
    const aiFeedbackProperties = getAiFeedbackProperties(
      feedbackMessage,
      displayedMessagesWithFeedback,
      session.id,
    );
    const feedbackSentiment = feedback === 'up' ? 'positive' : 'negative';
    const feedbackSubmissionId = createFeedbackSubmissionId(messageId, feedback);
    const feedbackProperties = {
      ...aiFeedbackProperties,
      entry_id: messageId,
      feedback,
      feedback_sentiment: feedbackSentiment,
      organization_id: activeOrganizationId,
      source: 'dashboard_assistant_frontend',
      thread_id: session.id,
    };
    setFeedbackByMessageId((current) => ({ ...current, [messageId]: feedback }));
    setFeedbackSavingByMessageId((current) => ({ ...current, [messageId]: true }));
    Sentry.addBreadcrumb({
      category: 'dashboard_assistant.feedback',
      data: {
        entryId: messageId,
        feedback,
        organizationId: activeOrganizationId,
        threadId: session.id,
      },
      level: 'info',
      message: 'Dashboard assistant feedback submitted',
    });
    if (posthog && aiFeedbackProperties.$ai_trace_id) {
      safeCapture(posthog, '$ai_feedback', {
        ...feedbackProperties,
        $ai_feedback_score: feedback === 'up' ? 1 : -1,
        $ai_feedback_text: feedbackSentiment,
        $ai_parent_id: aiFeedbackProperties.$ai_span_id || aiFeedbackProperties.$ai_trace_id,
        $ai_span_id: feedbackSubmissionId,
      });
    }
    if (posthog && DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID && aiFeedbackProperties.$ai_trace_id) {
      safeCapture(posthog, 'survey sent', {
        ...feedbackProperties,
        $survey_completed: true,
        $survey_id: DASHBOARD_ASSISTANT_FEEDBACK_SURVEY_ID,
        $survey_submission_id: feedbackSubmissionId,
        [`$survey_response_${DASHBOARD_ASSISTANT_FEEDBACK_SENTIMENT_QUESTION_ID}`]:
          feedbackSentiment,
      });
    }

    try {
      if (!activeOrganizationId) {
        return;
      }
      await setAssistantFeedback({
        entryId: messageId,
        feedback,
        organizationId: activeOrganizationId,
        threadId: session.id,
      });
    } catch (feedbackError) {
      console.warn('Failed to save dashboard assistant feedback:', feedbackError);
      Sentry.captureException(feedbackError, {
        tags: {
          feature: 'dashboard_assistant',
          operation: 'feedback.set',
        },
        extra: {
          entryId: messageId,
          feedback,
          organizationId: activeOrganizationId,
          threadId: session.id,
        },
      });
      safeCapture(posthog, 'dashboard_assistant_feedback_failed', {
        ...feedbackProperties,
        error_type: feedbackError instanceof Error ? feedbackError.name : 'unknown',
      });
    } finally {
      setFeedbackSavingByMessageId((current) => ({ ...current, [messageId]: false }));
    }
  };

  const handleCloseSession = () => {
    const historyMessages = isSessionHistoryRegression(
      session.historyMessages ?? [],
      displayedMessagesWithFeedback,
    )
      ? session.historyMessages
      : displayedMessagesWithFeedback;

    closeSession(
      session.id,
      historyMessages && historyMessages.length > 0
        ? {
            historyMessages,
            lastMessageAt: new Date().toISOString(),
          }
        : undefined,
    );
  };

  // Resolves to whether the message was sent, for "Use this list" (ADM-197).
  const handleSubmit = useCallback(async (messageOverride?: string): Promise<boolean> => {
    const nextMessage = (messageOverride ?? draft).trim();
    if (!nextMessage || isLoading) {
      return false;
    }

    if (session.title === 'New chat') {
      updateSession(session.id, { title: getSessionTitle(nextMessage) });
    }

    setInlineError(null);
    shouldStickToBottomRef.current = true;

    try {
      const sent = await sendMessage(nextMessage);
      // A skipped send (another one still starting) keeps the draft.
      if (sent && !messageOverride) clearSubmittedDraft(session.id, draft);
      return sent;
    } catch (sendError) {
      const message = sendError instanceof Error ? sendError.message : 'Chat failed to send.';
      setInlineError(message);
      // Keep the draft in the provider, including text edited while sending.
      return false;
    }
  }, [draft, isLoading, session.id, session.title, updateSession, sendMessage, clearSubmittedDraft]);

  useEffect(() => {
    if (!session.initialPrompt || isLoading || isReady === false) return;
    const prompt = claimInitialPrompt(session.id);
    if (!prompt) return;
    void handleSubmit(prompt).then((sent) => {
      if (sent) clearSubmittedDraft(session.id, prompt);
    });
  }, [session.id, session.initialPrompt, isLoading, isReady, claimInitialPrompt, handleSubmit, clearSubmittedDraft]);

  return (
    <div
      className={`flex min-h-0 flex-col overflow-hidden border border-edge/80 bg-white transition-[border-radius,box-shadow] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
        compact ? 'h-full w-full !rounded-none !border-0 !shadow-none' : isDocked
          ? 'ml-1 mr-2 h-full w-[calc(100%-0.75rem)] rounded-md shadow-[-12px_0_32px_rgba(58,52,41,0.08)]'
          : 'h-full w-full rounded-lg shadow-[0_24px_70px_rgba(58,52,41,0.16)]'
      }`}
    >
      {!compact && <div className="flex shrink-0 items-center gap-3 border-b border-edge/60 bg-paper-light px-5 py-5">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <div className="truncate text-base font-semibold text-ink">{panelTitle}</div>
            <span className="shrink-0 rounded-full border border-sage/25 bg-white px-2 py-0.5 text-[11px] font-medium leading-none text-sage">
              Beta
            </span>
          </div>
        </div>
        <button
          onClick={minimize}
          className={`h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-paper-inset hover:text-ink ${
            isMaximized ? 'hidden' : 'flex'
          }`}
          title="Minimize"
          type="button"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onToggleDocked}
          className="hidden h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-paper-inset hover:text-ink lg:flex"
          title={isDocked ? 'Return to floating chat' : 'Dock chat to right'}
          aria-label={isDocked ? 'Return to floating chat' : 'Dock chat to right'}
          type="button"
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.span
              key={isDocked ? 'floating-chat' : 'dock-chat'}
              initial={{ opacity: 0, rotate: isDocked ? -10 : 10, scale: 0.75 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: isDocked ? 10 : -10, scale: 0.75 }}
              transition={{ duration: 0.14, ease: 'easeOut' }}
              className="flex items-center justify-center"
            >
              {isDocked ? (
                <MessageSquare className="h-3.5 w-3.5" />
              ) : (
                <PanelRight className="h-3.5 w-3.5" />
              )}
            </motion.span>
          </AnimatePresence>
        </button>
        <button
          onClick={onToggleMaximized}
          className={`h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-paper-inset hover:text-ink ${
            isDocked ? 'hidden' : 'flex'
          }`}
          title={isMaximized ? 'Restore' : 'Maximize'}
          type="button"
        >
          {isMaximized ? (
            <Minimize2 className="h-3.5 w-3.5" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          onClick={handleCloseSession}
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-paper-inset hover:text-ink"
          title="Close chat"
          type="button"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>}

      <div
        ref={scrollContainerRef}
        onScroll={() => {
          const scrollContainer = scrollContainerRef.current;
          if (!scrollContainer) {
            return;
          }
          const distanceFromBottom =
            scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
          shouldStickToBottomRef.current = distanceFromBottom < 80;
        }}
        className={`min-h-0 flex-1 overflow-y-auto bg-white px-5 pb-0 pt-5 ${
          isMaximized ? 'flex flex-col' : ''
        }`}
      >
        {displayedMessages.length === 0 ? (
          <div className="flex h-full flex-col justify-end">
            <div className={`${isMaximized ? 'mx-auto w-full max-w-[760px]' : ''} space-y-5`}>
              <div className="w-full text-[15px] leading-8 text-ink">
                {isSmartTagging
                  ? "Let's tag some contacts. Describe the group and I'll find them, show you the list to review, and tag them once you confirm."
                  : 'I can help with CharityStack analytics, dashboard records, and workflows.'}
              </div>
              <div className="flex flex-col items-start gap-3">
                {emptyStateSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => void handleSubmit(suggestion)}
                    className="rounded-xl border border-edge bg-paper-light px-4 py-2.5 text-left text-sm text-ink shadow-sm transition hover:bg-paper-inset"
                    type="button"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={`${isMaximized ? 'mx-auto w-full max-w-[760px] flex-1' : ''} space-y-3`}>
            {displayedRenderItems.map((item, index) => {
              const isLatestMessage = index === displayedRenderItems.length - 1;
              if (item.type === 'activity') {
                return (
                  <DashboardActivityGroup
                    key={item.id}
                    isComplete={!isLoading || !isLatestMessage}
                    messages={item.messages}
                  />
                );
              }

              const message = item.message;
              const isUser = message.role === 'user';
              const precedingItem = index > 0 ? displayedRenderItems[index - 1] : undefined;
              const allowedDocumentationUrls =
                !isUser && precedingItem?.type === 'activity'
                  ? getDocumentationSourceUrls(precedingItem.messages)
                  : [];
              const messageKey = getDockMessageKey(message);
              const shouldAnimateAssistantText =
                !isUser &&
                isLatestMessage &&
                !isLoading &&
                message.kind !== 'action' &&
                !message.displayPayload &&
                Boolean(message.content) &&
                (animatingAssistantMessageKeys.has(messageKey) ||
                  !animatedAssistantMessageIdsRef.current.has(messageKey));
              const selectedFeedback = message.id
                ? (feedbackByMessageId[message.id] ?? message.feedback)
                : null;
              const isFeedbackSaving = message.id
                ? Boolean(feedbackSavingByMessageId[message.id])
                : false;
              const isStructuredAssistantMessage = !isUser && Boolean(message.displayPayload);
              return (
                <div
                  key={item.id}
                  className={`group flex ${isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex flex-col ${
                      isStructuredAssistantMessage || (isMaximized && !isUser)
                        ? 'w-full max-w-full'
                        : 'max-w-[85%]'
                    } ${isUser ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={
                        isUser
                          ? 'rounded-2xl bg-ink px-3.5 py-2.5 text-sm leading-6 text-white shadow-sm'
                          : isStructuredAssistantMessage
                            ? 'w-full text-sm leading-6 text-ink'
                            : 'w-full px-1 py-1 text-sm leading-6 text-ink'
                      }
                    >
                      {isUser ? (
                        <div className="space-y-2">
                          <div className="whitespace-pre-wrap break-words">{message.content}</div>
                        </div>
                      ) : (
                        <AssistantMessageContent
                          allowedDocumentationUrls={allowedDocumentationUrls}
                          animateText={shouldAnimateAssistantText}
                          content={message.content}
                          dashboardTimezone={dashboardTimezone}
                          merchantCountryCode={merchantCountryCode}
                          displayPayload={message.displayPayload}
                          isStreaming={isLoading && isLatestMessage}
                          threadId={conversationId}
                          onUseList={item.id === useListItemId ? handleSubmit : undefined}
                          useListDisabled={isLoading}
                          onAnimationComplete={() => {
                            animatedAssistantMessageIdsRef.current.add(messageKey);
                            setAnimatingAssistantMessageKeys((currentKeys) => {
                              if (!currentKeys.has(messageKey)) {
                                return currentKeys;
                              }

                              const nextKeys = new Set(currentKeys);
                              nextKeys.delete(messageKey);
                              return nextKeys;
                            });
                          }}
                        />
                      )}
                    </div>
                    {!isUser && message.id ? (
                      <div
                        className={`mt-1 flex items-center gap-1 pl-1 transition-opacity ${
                          selectedFeedback
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                        }`}
                      >
                        <button
                          onClick={() => void handleFeedback(message.id as string, 'up')}
                          className={`flex h-6 w-6 items-center justify-center rounded-md transition hover:bg-paper-light disabled:cursor-not-allowed disabled:opacity-60 ${
                            selectedFeedback === 'up'
                              ? 'text-sage'
                              : 'text-ink-muted hover:text-ink'
                          }`}
                          aria-pressed={selectedFeedback === 'up'}
                          disabled={isFeedbackSaving}
                          type="button"
                          aria-label="Mark response helpful"
                          title="Helpful"
                        >
                          <ThumbsUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => void handleFeedback(message.id as string, 'down')}
                          className={`flex h-6 w-6 items-center justify-center rounded-md transition hover:bg-paper-light disabled:cursor-not-allowed disabled:opacity-60 ${
                            selectedFeedback === 'down'
                              ? 'text-rose-600'
                              : 'text-ink-muted hover:text-ink'
                          }`}
                          aria-pressed={selectedFeedback === 'down'}
                          disabled={isFeedbackSaving}
                          type="button"
                          aria-label="Mark response not helpful"
                          title="Not helpful"
                        >
                          <ThumbsDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {isLoading ? <DashboardAssistantThinkingIndicator /> : null}

            <div ref={scrollAnchorRef} />
          </div>
        )}
      </div>

      <div className={`shrink-0 bg-white p-4 ${compact ? 'max-h-[70%] overflow-y-auto' : ''}`}>
        <div className={isMaximized ? 'mx-auto w-full max-w-[760px]' : ''}>
          {inlineError ? (
            <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {inlineError}
            </div>
          ) : null}

          {statusMessage || canCancelActiveRun ? (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-xl border border-edge bg-paper-light px-3 py-2 text-xs text-ink-muted">
              <span>{statusMessage || 'The assistant is working on your request.'}</span>
              {canCancelActiveRun ? (
                <button
                  className="shrink-0 font-medium text-ink hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={statusMessage === 'Cancelling this assistant request…'}
                  onClick={() => void cancelActiveRun()}
                  type="button"
                >
                  {statusMessage === 'Cancelling this assistant request…'
                    ? 'Cancelling…'
                    : 'Cancel'}
                </button>
              ) : null}
            </div>
          ) : null}

          {suggestedReplies.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-2 px-1">
              {suggestedReplies.map((reply) => (
                <button
                  key={`${reply.label}:${reply.prompt}`}
                  onClick={() => void handleSubmit(reply.prompt)}
                  className="inline-flex max-w-full items-center rounded-full border border-edge bg-paper-light/70 px-3 py-1.5 text-left text-xs font-medium leading-5 text-ink shadow-sm transition hover:bg-paper-light disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                  type="button"
                >
                  <span className="truncate">{reply.label}</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 rounded-2xl border border-edge/70 bg-white px-3 py-2 shadow-sm">
            <textarea
              ref={textareaRef}
              aria-label="Message CharityStack AI"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
              style={compact ? { maxHeight: 'clamp(40px, calc(var(--assistant-viewport-height) * 0.18), 144px)' } : undefined}
              rows={1}
              placeholder="Ask CharityStack Agent"
              className="block max-h-36 min-h-[40px] w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base lg:text-sm text-ink outline-none ring-0 placeholder:text-ink-muted focus:border-0 focus:outline-none focus:ring-0"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                aria-label="Send message"
                onClick={() => void handleSubmit()}
                disabled={!draft.trim() || isLoading}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-white transition hover:bg-ink/90 disabled:cursor-not-allowed disabled:bg-ink/40"
                type="button"
              >
                <ArrowUpRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getAiFeedbackProperties(
  message: DashboardAssistantDockMessage | undefined,
  messages: DashboardAssistantDockMessage[],
  sessionId: string,
) {
  const metadata = message?.metadata || {};
  const traceId =
    stringValue(metadata.aiTraceId) ||
    stringValue(metadata.$ai_trace_id) ||
    getFallbackTraceId(message, messages);
  const aiSessionId =
    stringValue(metadata.aiSessionId) || stringValue(metadata.$ai_session_id) || sessionId;
  const aiSpanId =
    stringValue(metadata.aiGenerationSpanId) ||
    stringValue(metadata.aiSpanId) ||
    stringValue(metadata.$ai_span_id);
  const posthogSessionId =
    stringValue(metadata.posthogSessionId) || stringValue(metadata.$session_id);
  const runId = message?.runId || stringValue(metadata.runId);

  return {
    $ai_session_id: aiSessionId,
    $ai_span_id: aiSpanId,
    $ai_trace_id: traceId,
    $session_id: posthogSessionId,
    run_id: runId,
  };
}

function getFallbackTraceId(
  message: DashboardAssistantDockMessage | undefined,
  messages: DashboardAssistantDockMessage[],
) {
  if (!message?.id) {
    return undefined;
  }

  const messageIndex = messages.findIndex((candidate) => candidate.id === message.id);
  if (messageIndex < 0) {
    return undefined;
  }

  for (let index = messageIndex - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (candidate.role === 'user' && candidate.id) {
      return candidate.id;
    }
  }

  return undefined;
}

function createFeedbackSubmissionId(messageId: string, feedback: 'up' | 'down') {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `dashboard-assistant-feedback-${messageId}-${feedback}-${randomId}`;
}

function stringValue(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
